import { useState, useEffect } from "react";
import { useHiringRequests } from "../../contexts/HiringRequestsContext"; // Hiring requests context
import { useMasters } from "../../contexts/MastersContext";
import { useRoleData } from "../../contexts/RoleDataContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";

const HiringRequestForm = ({
  closeModal,
  request,
  isEditing,
  setNotification,
}) => {
  const { addHiringRequest, updateHiringRequest } = useHiringRequests(); // Access add and update functions
  const { employmentTypes, workplaces, designations, loading, error } =
    useMasters(); // Access employment types, workplaces and designations from Masters context
  const { getHRUsers } = useRoleData();
  const hrUsers = getHRUsers();
  const [newRequest, setNewRequest] = useState({
    employment_type: "",
    designation: "",
    hiring_type: "",
    number_of_positions: "",
    location: "",
    stipend_ctc_per_month: "",
    minimum_qualification: "",
    years_experience_required: "",
    skill_set: "",
    tat_days_to_close: "",
    interview_rounds: "",
    other_notes: "",
    job_description: "",
    kra_kpi: null,
    technical_test_path: null,
    to_hr: "",
    replaced_employee_name: "",
  });
  const [errors, setErrors] = useState({}); // Validation errors
  const [loadingState, setLoadingState] = useState(false); // Local loading state
  const [existingFiles, setExistingFiles] = useState({
    job_description_url: "",
    kra_kpi_url: "",
    technical_test_path_url: "",
  });
  const [interviewRoundsFlow, setInterviewRoundsFlow] = useState([]); // Interview rounds configuration
  const [designationSearch, setDesignationSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [showDesignationDropdown, setShowDesignationDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Helper function to get ID from name or return the value if it's already an ID
  const getIdFromNameOrValue = (value, options) => {
    if (!value) return "";
    if (!options || options.length === 0) return String(value); // Return original value if options not loaded yet

    // First, check if the value is already an ID (matches an option's ID)
    const foundById = options.find(
      (option) => String(option.id) === String(value) || option.id === value,
    );
    if (foundById) return String(foundById.id);

    // If not found by ID, try to find by name
    const foundByName = options.find(
      (option) =>
        String(option.name).toLowerCase().trim() ===
        String(value).toLowerCase().trim(),
    );
    if (foundByName) return String(foundByName.id);

    // If still not found, return the original value (might be an ID that doesn't exist in current options)
    return String(value);
  };

  // Populate form fields when editing
  useEffect(() => {
    if (isEditing && request) {
      // Get IDs for dropdown fields - handle both name and ID values
      // Only convert if master data is available
      const employmentTypeId = getIdFromNameOrValue(
        request.employment_type,
        employmentTypes && employmentTypes.length > 0 ? employmentTypes : [],
      );
      const designationId = getIdFromNameOrValue(
        request.designation,
        designations && designations.length > 0 ? designations : [],
      );
      const locationId = getIdFromNameOrValue(
        request.location,
        workplaces && workplaces.length > 0 ? workplaces : [],
      );

      setNewRequest({
        employment_type: employmentTypeId,
        designation: designationId,
        hiring_type: request.hiring_type || "",
        number_of_positions: request.number_of_positions || "",
        location: locationId,
        stipend_ctc_per_month: request.stipend_ctc_per_month || "",
        minimum_qualification: request.minimum_qualification || "",
        years_experience_required: request.years_experience_required || "",
        skill_set: request.skill_set || "",
        tat_days_to_close: request.tat_days_to_close || "",
        interview_rounds: request.interview_rounds || "",
        other_notes: request.other_notes || "",
        job_description: request.job_description || "",
        kra_kpi: null, // Will be handled separately for file display
        technical_test_path: null, // Will be handled separately for file display
        to_hr: request.to_hr || "",
        replaced_employee_name: request.replaced_employee_name || "",
      });

      // Set existing file URLs for display
      setExistingFiles({
        job_description_url: request.job_description_url || "",
        kra_kpi_url: request.kra_kpi_url || "",
        technical_test_path_url: request.technical_test_path_url || "",
      });
    }
  }, [isEditing, request, employmentTypes, designations, workplaces]);

  // Update interview rounds flow when interview_rounds changes
  useEffect(() => {
    const roundsCount = parseInt(newRequest.interview_rounds) || 0;
    if (roundsCount > 0) {
      // Initialize or update rounds array
      setInterviewRoundsFlow((prev) => {
        // If we already have the correct number of rounds and they match, don't reset
        if (prev.length === roundsCount) {
          // Check if any round numbers don't match
          const needsReset = prev.some((r, idx) => r.round_number !== idx + 1);
          if (!needsReset) {
            return prev; // Keep existing rounds
          }
        }

        const newRounds = [];
        for (let i = 1; i <= roundsCount; i++) {
          // If editing and existing round_type_mapping exists, use it; otherwise check prev
          let existingRoundType = "";
          if (isEditing && request?.round_type_mapping) {
            // Handle array, object, and string (JSON) formats
            try {
              let mapping = request.round_type_mapping;

              // If it's a string, try to parse it
              if (typeof mapping === "string") {
                mapping = JSON.parse(mapping);
              }

              // Handle array format: ["Managerial", "HR"] -> index 0 = Round 1, index 1 = Round 2
              if (Array.isArray(mapping)) {
                existingRoundType = mapping[i - 1] || ""; // i-1 because array is 0-indexed, rounds are 1-indexed
              }
              // Handle object format: {"1": "Managerial", "2": "HR"}
              else if (typeof mapping === "object" && mapping !== null) {
                existingRoundType = mapping[String(i)] || mapping[i] || "";
              }
            } catch (error) {
              console.error("Error parsing round_type_mapping:", error);
              existingRoundType = "";
            }
          }

          // If not found in request, check previous state
          if (!existingRoundType) {
            const existingRound = prev.find((r) => r.round_number === i);
            existingRoundType = existingRound?.round_type || "";
          }

          newRounds.push({
            round_number: i,
            round_type: existingRoundType,
          });
        }
        return newRounds;
      });
    } else {
      setInterviewRoundsFlow([]);
    }
  }, [newRequest.interview_rounds, isEditing, request]);

  // Handle form changes with real-time validation
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for interview_rounds to reset flow
    if (name === "interview_rounds") {
      setNewRequest((prev) => ({
        ...prev,
        [name]: value,
      }));
      // The useEffect will handle updating interviewRoundsFlow
      return;
    }

    setNewRequest((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Real-time validation for length limits
    const textFields = ["minimum_qualification"];
    const textareaFields = ["skill_set", "other_notes"];

    // Field name mapping for better error messages
    const fieldNameMap = {
      minimum_qualification: "Minimum qualification",
      skill_set: "Skill set",
      other_notes: "Additional notes & suggestions",
      replaced_employee_name: "Replaced employee name",
    };

    if (name === "hiring_type" && value !== "Replacement") {
      setNewRequest((prev) => ({
        ...prev,
        replaced_employee_name: "",
      }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.replaced_employee_name;
        return newErrors;
      });
    }

    if ((textFields.includes(name) || name === "replaced_employee_name") && value.length > 50) {
      const fieldName =
        fieldNameMap[name] ||
        name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      setErrors((prev) => ({
        ...prev,
        [name]: `${fieldName} must be maximum 50 characters.`,
      }));
    } else if (textareaFields.includes(name) && value.length > 300) {
      const fieldName =
        fieldNameMap[name] ||
        name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      setErrors((prev) => ({
        ...prev,
        [name]: `${fieldName} must be maximum 300 characters.`,
      }));
    } else {
      // Clear error for this field if it's now valid
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Handle blur events to validate fields when user leaves them
  const handleBlur = (e) => {
    const { name } = e.target;
    const validationErrors = validate();
    // Update errors for this specific field
    setErrors((prev) => {
      const newErrors = { ...prev };

      // Auto-resolve search queries to IDs for Designation and Location if not already set
      if (name === "designation" && !newRequest.designation && designationSearch) {
        const found = designations.find(
          (d) => d.name.toLowerCase() === designationSearch.toLowerCase().trim()
        );
        if (found) {
          setNewRequest((prevReq) => ({ ...prevReq, designation: String(found.id) }));
          setDesignationSearch(found.name);
          delete newErrors.designation;
          return newErrors;
        }
      }

      if (name === "location" && !newRequest.location && locationSearch) {
        const found = workplaces.find(
          (w) => w.name.toLowerCase() === locationSearch.toLowerCase().trim()
        );
        if (found) {
          setNewRequest((prevReq) => ({ ...prevReq, location: String(found.id) }));
          setLocationSearch(found.name);
          delete newErrors.location;
          return newErrors;
        }
      }

      if (validationErrors[name]) {
        newErrors[name] = validationErrors[name];
      } else {
        // Clear error if field is now valid
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  // Get available round types for a specific round (excluding already selected ones)
  const getAvailableRoundTypes = (currentRoundIndex) => {
    const allRoundTypes = [
      "Screening",
      "Technical",
      "HR",
      "Managerial",
      "Final",
    ];
    const selectedTypes = interviewRoundsFlow
      .map((round, index) =>
        index !== currentRoundIndex ? round.round_type : null,
      )
      .filter((type) => type && type !== "");
    return allRoundTypes.filter((type) => !selectedTypes.includes(type));
  };

  // Handle interview round flow changes
  const handleInterviewRoundChange = (roundIndex, field, value) => {
    setInterviewRoundsFlow((prev) => {
      const updated = [...prev];
      updated[roundIndex] = {
        ...updated[roundIndex],
        [field]: value,
      };
      return updated;
    });

    // Clear error for this round if exists
    const errorKey = `interview_round_${roundIndex}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // Handle file changes
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0] || null;
    setNewRequest((prev) => ({
      ...prev,
      [name]: file,
    }));

    // Validate file immediately if selected
    if (file) {
      const validationErrors = validate();
      setErrors((prev) => ({
        ...prev,
        [name]: validationErrors[name] || null,
      }));
    } else {
      // Clear error if file is removed
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Helper function to strip HTML and get plain text length
  const getPlainTextLength = (htmlString) => {
    if (!htmlString) return 0;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return (tempDiv.textContent || tempDiv.innerText || "").trim().length;
  };

  const validate = () => {
    const validationErrors = {};

    // Employment Type validation
    if (!newRequest.employment_type) {
      validationErrors.employment_type = "Employment type is required.";
    }

    // Designation validation
    if (!newRequest.designation || !String(newRequest.designation).trim()) {
      validationErrors.designation = "Designation is required.";
    }

    // Hiring Type validation
    if (!newRequest.hiring_type) {
      validationErrors.hiring_type = "Hiring type is required.";
    }

    // Number of Positions validation
    if (
      !newRequest.number_of_positions ||
      isNaN(newRequest.number_of_positions) ||
      newRequest.number_of_positions < 1
    ) {
      validationErrors.number_of_positions =
        "Number of positions must be at least 1.";
    }

    if (!newRequest.location || !String(newRequest.location).trim()) {
      validationErrors.location = "Location is required.";
    }

    // Replaced Employee Name validation
    if (newRequest.hiring_type === "Replacement") {
      if (!newRequest.replaced_employee_name || !newRequest.replaced_employee_name.trim()) {
        validationErrors.replaced_employee_name = "Replaced employee name is required.";
      } else if (newRequest.replaced_employee_name.length > 50) {
        validationErrors.replaced_employee_name = "Replaced employee name must be maximum 50 characters.";
      }
    }

    // Stipend/CTC validation
    if (
      !newRequest.stipend_ctc_per_month ||
      newRequest.stipend_ctc_per_month === ""
    ) {
      validationErrors.stipend_ctc_per_month =
        "Stipend/CTC per month is required.";
    } else if (
      isNaN(newRequest.stipend_ctc_per_month) ||
      newRequest.stipend_ctc_per_month < 0
    ) {
      validationErrors.stipend_ctc_per_month =
        "Stipend/CTC must be a positive number.";
    }

    if (!newRequest.minimum_qualification || !newRequest.minimum_qualification.trim()) {
      validationErrors.minimum_qualification =
        "Minimum qualification is required.";
    } else if (String(newRequest.minimum_qualification).length > 50) {
      validationErrors.minimum_qualification =
        "Minimum qualification must be maximum 50 characters.";
    }

    // Years Experience validation
    if (
      !newRequest.years_experience_required ||
      newRequest.years_experience_required === ""
    ) {
      validationErrors.years_experience_required =
        "Years experience is required.";
    } else if (
      isNaN(newRequest.years_experience_required) ||
      newRequest.years_experience_required < 0 ||
      newRequest.years_experience_required > 50
    ) {
      validationErrors.years_experience_required =
        "Years experience must be a number between 0 and 50.";
    }

    // Skill Set validation
    if (!newRequest.skill_set || !newRequest.skill_set.trim()) {
      validationErrors.skill_set = "Skill set is required.";
    } else if (String(newRequest.skill_set).length > 300) {
      validationErrors.skill_set = "Skill set must be maximum 300 characters.";
    }

    // TAT Days validation
    if (!newRequest.tat_days_to_close || newRequest.tat_days_to_close === "") {
      validationErrors.tat_days_to_close = "TAT days to close is required.";
    } else if (
      isNaN(newRequest.tat_days_to_close) ||
      newRequest.tat_days_to_close < 0
    ) {
      validationErrors.tat_days_to_close =
        "TAT days must be a positive number.";
    }

    // to_hr validation
    if (!newRequest.to_hr) {
      validationErrors.to_hr = "Assign to HR is required.";
    }

    // Interview Rounds validation
    if (!newRequest.interview_rounds) {
      validationErrors.interview_rounds = "Interview rounds is required.";
    } else {
      // Validate interview rounds flow
      const roundsCount = parseInt(newRequest.interview_rounds) || 0;
      if (roundsCount > 0 && interviewRoundsFlow.length !== roundsCount) {
        validationErrors.interview_rounds =
          "Please configure all interview rounds.";
      }

      // Validate each round
      interviewRoundsFlow.forEach((round, index) => {
        if (!round.round_type) {
          validationErrors[`interview_round_${index}_round_type`] =
            `Round ${round.round_number} type is required.`;
        }
      });
    }

    // Job Description validation (required)
    const jobDescPlainTextLength = getPlainTextLength(
      newRequest.job_description,
    );
    if (!newRequest.job_description.trim() || jobDescPlainTextLength === 0) {
      validationErrors.job_description = "Job description is required.";
    } else if (jobDescPlainTextLength > 5000) {
      validationErrors.job_description =
        "Job description must be maximum 5000 characters.";
    }

    // Additional Notes & Suggestions validation (optional but has max length)
    if (newRequest.other_notes && newRequest.other_notes.length > 300) {
      validationErrors.other_notes =
        "Additional notes & suggestions must be maximum 300 characters.";
    }

    if (newRequest.kra_kpi) {
      const file = newRequest.kra_kpi;
      const maxSize = 2 * 1024 * 1024; // 2 MB

      if (file.size > maxSize) {
        validationErrors.kra_kpi = "File size must be less than 2 MB.";
      } else if (file.type !== "application/pdf") {
        validationErrors.kra_kpi = "File must be PDF.";
      }
    }

    if (newRequest.technical_test_path) {
      const file = newRequest.technical_test_path;
      const maxSize = 2 * 1024 * 1024; // 2 MB

      if (file.size > maxSize) {
        validationErrors.technical_test_path =
          "File size must be less than 2 MB.";
      } else if (file.type !== "application/pdf") {
        validationErrors.technical_test_path = "File must be PDF.";
      }
    }

    return validationErrors;
  };

  // Check if form is valid (all required fields filled and no validation errors)
  const isFormValid = () => {
    // Check all required fields
    const requiredFields = {
      employment_type: newRequest.employment_type || "",
      designation: String(newRequest.designation || "").trim(),
      hiring_type: newRequest.hiring_type || "",
      number_of_positions: newRequest.number_of_positions || "",
      location: String(newRequest.location || "").trim(),
      ...(newRequest.hiring_type === "Replacement" && {
        replaced_employee_name: String(newRequest.replaced_employee_name || "").trim(),
      }),
      stipend_ctc_per_month: newRequest.stipend_ctc_per_month || "",
      minimum_qualification: String(newRequest.minimum_qualification || "").trim(),
      years_experience_required: newRequest.years_experience_required || "",
      skill_set: String(newRequest.skill_set || "").trim(),
      tat_days_to_close: newRequest.tat_days_to_close || "",
      interview_rounds: newRequest.interview_rounds || "",
      job_description: String(newRequest.job_description || "").trim(),
      to_hr: newRequest.to_hr || "",
    };

    // Check if all required fields are filled
    for (const [key, value] of Object.entries(requiredFields)) {
      if (!value) {
        return false;
      }
    }

    // Check for validation errors
    const validationErrors = validate();
    return Object.keys(validationErrors).length === 0;
  };

  // new code
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submission started");

    const validationErrors = validate();
    console.log("Validation errors:", validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      console.log("Form has validation errors, stopping submission");

      // Scroll to first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`,
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }

      return;
    }

    console.log("Form data to submit:", newRequest);

    // Log FormData contents for debugging
    const formData = new FormData();
    Object.keys(newRequest).forEach((key) => {
      if (newRequest[key] !== null && typeof newRequest[key] !== "object") {
        formData.append(key, newRequest[key]);
      }
    });
    if (newRequest.job_description) {
      formData.append("job_description", newRequest.job_description);
    }
    if (newRequest.kra_kpi) {
      formData.append("kra_kpi", newRequest.kra_kpi);
    }
    if (newRequest.technical_test_path) {
      formData.append("technical_test_path", newRequest.technical_test_path);
    }

    // Build round_type_mapping object from interviewRoundsFlow
    if (interviewRoundsFlow.length > 0) {
      const roundTypeMapping = {};
      interviewRoundsFlow.forEach((round) => {
        if (round.round_type) {
          roundTypeMapping[String(round.round_number)] = round.round_type;
        }
      });
      formData.append("round_type_mapping", JSON.stringify(roundTypeMapping));
    }

    console.log("FormData entries:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    setLoadingState(true); // Start loading
    try {
      if (isEditing && request) {
        formData.append("id", request.id);
        // formData.append('status', request.status);
        console.log("Updating hiring request with FormData");
        const result = await updateHiringRequest(formData);
        console.log("Update result:", result);
        if (!result.success) {
          setNotification({
            message: result.error,
            type: "error",
          });
          return;
        } else {
          setNotification({
            message: "Hiring request updated successfully",
            type: "success",
          });
        }
      } else {
        console.log("Adding new hiring request with FormData");
        const result = await addHiringRequest(formData);
        console.log("Add result:", result);
        if (!result.success) {
          setNotification({
            message: result.error,
            type: "error",
          });
          return;
        } else {
          setNotification({
            message: "Hiring request added successfully",
            type: "success",
          });
        }
      }

      setNewRequest({
        employment_type: "",
        designation: "",
        hiring_type: "",
        number_of_positions: "",
        location: "",
        stipend_ctc_per_month: "",
        minimum_qualification: "",
        years_experience_required: "",
        skill_set: "",
        tat_days_to_close: "",
        interview_rounds: "",
        other_notes: "",
        job_description: "",
        kra_kpi: null,
        technical_test_path: null,
        to_hr: "",
      });

      setInterviewRoundsFlow([]);
      setErrors({});
      closeModal(); // Close modal after successful submission
    } catch (error) {
      console.error("Error submitting hiring request:", error);
      setNotification({
        message: `Error submitting form: ${error.message}`,
        type: "error",
      });
    } finally {
      setLoadingState(false); // Stop loading
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      {/* Position & Employment Details */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white">
            Employment Details
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Employment Type */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Employment Type
              <span className="text-red-500 ml-1">*</span>
            </label>
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-gray-500">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Loading employment types...
              </div>
            ) : error ? (
              <p className="text-red-500 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                {error}
              </p>
            ) : (
              <select
                name="employment_type"
                value={newRequest.employment_type}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.employment_type
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white`}
                required
              >
                <option value="">Select Employment Type</option>
                {employmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            )}
            {errors.employment_type && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.employment_type}
              </p>
            )}
          </div>

          {/* Designation */}
          <div className="space-y-2 relative">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Designation
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={
                  designationSearch ||
                  (newRequest.designation
                    ? designations.find(
                        (d) => String(d.id) === String(newRequest.designation),
                      )?.name || ""
                    : "")
                }
                onChange={(e) => {
                  setDesignationSearch(e.target.value);
                  setShowDesignationDropdown(true);
                  if (!e.target.value) {
                    setNewRequest((prev) => ({ ...prev, designation: "" }));
                  }
                }}
                onFocus={() => setShowDesignationDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowDesignationDropdown(false), 200);
                  handleBlur({ target: { name: "designation" } });
                }}
                placeholder="Search or select designation"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.designation
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {showDesignationDropdown &&
                designations &&
                designations.length > 0 && (
                  <div className="absolute z-[10001] w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                    {designations
                      .filter((designation) =>
                        designation.name
                          .toLowerCase()
                          .includes(designationSearch.toLowerCase()),
                      )
                      .map((designation) => (
                        <div
                          key={designation.id}
                          onClick={() => {
                            setNewRequest((prev) => ({
                              ...prev,
                              designation: String(designation.id),
                            }));
                            setDesignationSearch(designation.name);
                            setShowDesignationDropdown(false);
                            // Clear error immediately when selected
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.designation;
                              return newErrors;
                            });
                          }}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white transition-colors"
                        >
                          {designation.name}
                        </div>
                      ))}
                    {designations.filter((d) =>
                      d.name
                        .toLowerCase()
                        .includes(designationSearch.toLowerCase()),
                    ).length === 0 && (
                      <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        No results found
                      </div>
                    )}
                  </div>
                )}
            </div>
            {errors.designation && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.designation}
              </p>
            )}
          </div>

          {/* Hiring Type */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Hiring Type
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              name="hiring_type"
              value={newRequest.hiring_type}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.hiring_type
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white`}
              required
            >
              <option value="">Select Hiring Type</option>
              <option value="New">New</option>
              <option value="Replacement">Replacement</option>
            </select>
            {errors.hiring_type && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.hiring_type}
              </p>
            )}
          </div>

          {/* Replaced Employee Name (Conditional) */}
          {newRequest.hiring_type === "Replacement" && (
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                Replaced Employee Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="replaced_employee_name"
                value={newRequest.replaced_employee_name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter replaced employee name"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.replaced_employee_name
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.replaced_employee_name && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  {errors.replaced_employee_name}
                </p>
              )}
            </div>
          )}

          {/* Number of Positions */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Number of Positions
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="number"
              name="number_of_positions"
              value={newRequest.number_of_positions}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="1"
              min="1"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.number_of_positions
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.number_of_positions && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.number_of_positions}
              </p>
            )}
          </div>

          {/* Assign to HR */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Assign to HR
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              name="to_hr"
              value={newRequest.to_hr}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.to_hr
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white`}
              required
            >
              <option value="">Select HR</option>
              {hrUsers.map((hr) => (
                <option key={hr.user_id || hr.id} value={hr.user_id || hr.id}>
                  {hr.full_name || hr.name} ({hr.employee_id})
                </option>
              ))}
            </select>
            {errors.to_hr && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.to_hr}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Location & Compensation Details */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-md flex items-center justify-center">
            <svg
              className="w-4 h-4 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              ></path>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              ></path>
            </svg>
          </div>
          <h3 className="text-base font-semibold text-gray-800 dark:text-white">
            Location & Compensation Details
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Location */}
          <div className="space-y-2 relative">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Location
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={
                  locationSearch ||
                  (newRequest.location
                    ? workplaces.find(
                        (w) => String(w.id) === String(newRequest.location),
                      )?.name || ""
                    : "")
                }
                onChange={(e) => {
                  setLocationSearch(e.target.value);
                  setShowLocationDropdown(true);
                  if (!e.target.value) {
                    setNewRequest((prev) => ({ ...prev, location: "" }));
                  }
                }}
                onFocus={() => setShowLocationDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowLocationDropdown(false), 200);
                  handleBlur({ target: { name: "location" } });
                }}
                placeholder="Search or select location"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.location
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {showLocationDropdown && workplaces && workplaces.length > 0 && (
                <div className="absolute z-[10001] w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                  {workplaces
                    .filter((workplace) =>
                      workplace.name
                        .toLowerCase()
                        .includes(locationSearch.toLowerCase()),
                    )
                    .map((workplace) => (
                      <div
                        key={workplace.id}
                        onClick={() => {
                          setNewRequest((prev) => ({
                            ...prev,
                            location: String(workplace.id),
                          }));
                          setLocationSearch(workplace.name);
                          setShowLocationDropdown(false);
                          // Clear error immediately when selected
                          setErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.location;
                            return newErrors;
                          });
                        }}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white transition-colors"
                      >
                        {workplace.name}
                      </div>
                    ))}
                  {workplaces.filter((w) =>
                    w.name.toLowerCase().includes(locationSearch.toLowerCase()),
                  ).length === 0 && (
                    <div className="px-3 py-2 text-gray-500 dark:text-gray-400">
                      No results found
                    </div>
                  )}
                </div>
              )}
            </div>
            {errors.location && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.location}
              </p>
            )}
          </div>

          {/* Stipend/CTC per Month */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              ₹ Stipend/CTC per Month
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="number"
              name="stipend_ctc_per_month"
              value={newRequest.stipend_ctc_per_month}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="0"
              min="0"
              step="0.01"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.stipend_ctc_per_month
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.stipend_ctc_per_month && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.stipend_ctc_per_month}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Requirements & Skills */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white">
            Eligibility Criteria
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Minimum Qualification */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Minimum Qualification
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              name="minimum_qualification"
              value={newRequest.minimum_qualification}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g Bachelor's Degree, Master's Degree"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.minimum_qualification
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.minimum_qualification && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.minimum_qualification}
              </p>
            )}
          </div>

          {/* Years Experience Required */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Minimum Years of Experience Required
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="number"
              name="years_experience_required"
              value={newRequest.years_experience_required}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g 2"
              min="0"
              max="50"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.years_experience_required
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.years_experience_required && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.years_experience_required}
              </p>
            )}
          </div>
        </div>

        {/* Skill Set */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            Skill Set
            <span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            name="skill_set"
            value={newRequest.skill_set}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="e.g  React, Node.js, MongoDB, TypeScript, AWS"
            rows="3"
            className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
              errors.skill_set
                ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
            } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
            required
          />
          <div className="flex justify-between items-center mt-1">
            <span
              className={`text-xs ${
                newRequest.skill_set.length > 300
                  ? "text-red-500"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {newRequest.skill_set.length} / 300 characters
            </span>
          </div>
          {errors.skill_set && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {errors.skill_set}
            </p>
          )}
        </div>
      </div>

      {/* Interview & Process Details */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white">
            Interview & Process Details
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* TAT Days to Close */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              TAT Days to Close
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="number"
              name="tat_days_to_close"
              value={newRequest.tat_days_to_close}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="0"
              min="0"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.tat_days_to_close
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.tat_days_to_close && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.tat_days_to_close}
              </p>
            )}
          </div>

          {/* Interview Rounds */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Interview Rounds
              <span className="text-red-500 ml-1">*</span>
            </label>
            <select
              name="interview_rounds"
              value={newRequest.interview_rounds}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.interview_rounds
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white`}
              required
            >
              <option value="">Select Interview Rounds</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
            {errors.interview_rounds && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.interview_rounds}
              </p>
            )}
          </div>
        </div>

        {/* Interview Rounds Flow Configuration */}
        {newRequest.interview_rounds &&
          parseInt(newRequest.interview_rounds) > 0 && (
            <div className="space-y-4 mt-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-base font-semibold text-gray-800 dark:text-white">
                  Interview Rounds Flow
                </h3>
              </div>

              <div className="space-y-4">
                {interviewRoundsFlow.map((round, index) => (
                  <div
                    key={round.round_number}
                    className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50"
                  >
                    {/* Round Type - Label and Select on same line */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold">
                            {round.round_number}
                          </span>
                        </div>
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          Round Type
                          <span className="text-red-500 ml-1">*</span>
                        </label>
                      </div>
                      <div className="flex-1">
                        <select
                          value={round.round_type}
                          onChange={(e) =>
                            handleInterviewRoundChange(
                              index,
                              "round_type",
                              e.target.value,
                            )
                          }
                          className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                            errors[`interview_round_${index}_round_type`]
                              ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                          } dark:bg-gray-800 dark:text-white`}
                          required
                        >
                          <option value="">Select Round Type</option>
                          {getAvailableRoundTypes(index).map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                          {/* Show currently selected type even if it's in other rounds (for display purposes) */}
                          {round.round_type &&
                            !getAvailableRoundTypes(index).includes(
                              round.round_type,
                            ) && (
                              <option value={round.round_type} disabled>
                                {round.round_type} (Selected)
                              </option>
                            )}
                        </select>
                      </div>
                    </div>
                    {errors[`interview_round_${index}_round_type`] && (
                      <p className="text-red-500 text-sm flex items-center gap-1 mt-2">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          ></path>
                        </svg>
                        {errors[`interview_round_${index}_round_type`]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Job Description */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            Job Description
            <span className="text-red-500 ml-1">*</span>
          </label>

          <div className="relative">
            <ReactQuill
              theme="snow"
              value={newRequest.job_description}
              onChange={(content) => {
                setNewRequest((prev) => ({
                  ...prev,
                  job_description: content,
                }));

                // Real-time validation for job description length
                const plainTextLength = getPlainTextLength(content);
                if (plainTextLength > 5000) {
                  setErrors((prev) => ({
                    ...prev,
                    job_description:
                      "Job description must be maximum 5000 characters.",
                  }));
                } else {
                  // Clear error if it's now valid
                  if (errors.job_description) {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.job_description;
                      return newErrors;
                    });
                  }
                }
              }}
              className={`bg-white dark:bg-gray-800 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                errors.job_description
                  ? "border-red-400"
                  : "border-gray-200 dark:border-gray-600"
              }`}
              style={{
                minHeight: "120px",
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  ["link", "image"],
                  ["clean"],
                ],
              }}
              formats={[
                "header",
                "bold",
                "italic",
                "underline",
                "strike",
                "list",
                "bullet",
                "color",
                "background",
                "align",
                "link",
                "image",
              ]}
            />
            <div className="flex justify-between items-center mt-1">
              <span
                className={`text-xs ${
                  getPlainTextLength(newRequest.job_description) > 5000
                    ? "text-red-500"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {getPlainTextLength(newRequest.job_description)} /5000
                characters
              </span>
            </div>
            {errors.job_description && (
              <div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.job_description}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Notes & Suggestions */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white">
            Additional Notes & Suggestions
          </h3>
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
            Additional Notes & Suggestions
          </label>
          <textarea
            name="other_notes"
            value={newRequest.other_notes}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Any additional notes, comments, or suggestions..."
            rows="4"
            className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
              errors.other_notes
                ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
            } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
          />
          <div className="flex justify-between items-center mt-1">
            <span
              className={`text-xs ${
                newRequest.other_notes.length > 300
                  ? "text-red-500"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {newRequest.other_notes.length} / 300
            </span>
          </div>
          {errors.other_notes && (
            <p className="text-red-500 text-sm flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
              {errors.other_notes}
            </p>
          )}
        </div>
      </div>

      {/* File Uploads */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white">
            File Uploads
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* KRA/KPI File */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              KRA/KPI File
              <span className="text-xs text-gray-500 ml-1">
                (PDF - Max 2 MB)
              </span>
            </label>

            {/* Show existing file if available */}
            {isEditing && existingFiles.kra_kpi_url && (
              <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                  Current file:
                </p>
                <a
                  href={existingFiles.kra_kpi_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                  </svg>
                  View Current File
                </a>
              </div>
            )}

            <input
              type="file"
              name="kra_kpi"
              onChange={handleFileChange}
              accept=".pdf"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.kra_kpi
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400`}
            />
            {errors.kra_kpi && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.kra_kpi}
              </p>
            )}
          </div>

          {/* Technical Test Path */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              Technical Test Path
              <span className="text-xs text-gray-500 ml-1">
                (PDF - Max 2 MB)
              </span>
            </label>

            {/* Show existing file if available */}
            {isEditing && existingFiles.technical_test_path_url && (
              <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 mb-2">
                  Current file:
                </p>
                <a
                  href={existingFiles.technical_test_path_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    ></path>
                  </svg>
                  View Current File
                </a>
              </div>
            )}

            <input
              type="file"
              name="technical_test_path"
              onChange={handleFileChange}
              accept=".pdf"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.technical_test_path
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400`}
            />
            {errors.technical_test_path && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                {errors.technical_test_path}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end items-center gap-2 mt-6">
        <button
          type="button"
          onClick={closeModal}
          className="mt-1 px-8 py-2 font-medium hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!isFormValid() || loadingState}
          className={`px-8 py-2 font-medium rounded-md transition-all duration-200 ${
            !isFormValid() || loadingState
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {isEditing ? "Update Request" : "Create Request"}
        </button>
      </div>

      {/* <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            disabled={loadingState}
            // onClick={() => console.log("Submit button clicked")}
            className={`flex justify-end items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform ${
              loadingState
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:scale-[1.01] active:scale-[0.99] shadow-md hover:shadow-lg"
            } focus:outline-none focus:ring-4 focus:ring-blue-500/25`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isEditing ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              )}
            </svg>
            <span>
              {isEditing ? "Update Request" : "Create Request"}
            </span>
            {loadingState && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            )}
          </button>
        </div> */}
    </form>
  );
};

export default HiringRequestForm;
